// frontend/src/app/components/projects/project-corpus/dialogs/pdf-form.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, inject, signal } from '@angular/core';
import { ProjectCorpusService } from '../../../../project-corpus/domain/project-corpus.service';
import { ToastService } from '../../../../services/toast.service';

/** Per-file size cap (100 MB), matching the design spec. */
const MAX_PDF_BYTES = 100 * 1024 * 1024;

/**
 * PDF upload form for the Add-papers wizard. Multi-file selection with a
 * 100 MB per-file cap. Files that exceed the cap are refused and an inline
 * warning lists their names.
 *
 * Responsive behaviour:
 *  - Drop zone is full-width on every viewport.
 *  - Selected-file rows stack vertically (`flex flex-col gap-1`); each row's
 *    name truncates and the remove button stays right-aligned.
 *  - Submit row is `flex-col` on mobile and `sm:flex-row` on desktop so the
 *    primary action is full-width on phones.
 */
@Component({
  selector: 'app-pdf-form',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pdf-form.component.html',
  styleUrls: ['./pdf-form.component.scss'],
})
export class PdfFormComponent {
  private readonly service = inject(ProjectCorpusService);
  private readonly toast = inject(ToastService);

  readonly files = signal<File[]>([]);
  readonly fileErrors = signal<string[]>([]);
  readonly dragOver = signal(false);
  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);

  @Output() done = new EventEmitter<void>();

  onPick(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    this.handleSelection(files);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(false);
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    this.handleSelection(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(false);
  }

  removeAt(index: number): void {
    this.files.update((arr) => arr.filter((_, i) => i !== index));
  }

  clearAll(): void {
    this.files.set([]);
    this.fileErrors.set([]);
  }

  submit(): void {
    if (this.submitting()) return;
    const list = this.files();
    if (list.length === 0) return;
    this.submitting.set(true);
    this.submitError.set(null);
    this.service.createImportJob({ source: 'PDF', files: list }).subscribe({
      next: (job) => {
        this.submitting.set(false);
        const rejected = job.rejectedItems ?? [];
        if (rejected.length > 0) {
          // Tell the user which files were rejected and why, instead of silently dropping them.
          const summary = rejected.map((r) => `${r.filename} — ${r.message}`).join('; ');
          this.toast.error(
            `${rejected.length} file${rejected.length > 1 ? 's were' : ' was'} not imported`,
            summary,
          );
        } else {
          this.toast.success('Upload received', 'Files uploaded; metadata extraction is running.');
        }
        this.done.emit();
      },
      error: (err: unknown) => {
        this.submitError.set(this.toMessage(err));
        this.submitting.set(false);
      },
    });
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private handleSelection(picked: File[]): void {
    if (picked.length === 0) return;
    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const f of picked) {
      if (f.size > MAX_PDF_BYTES) {
        rejected.push(`${f.name} (${this.formatSize(f.size)}) exceeds the 100 MB limit.`);
        continue;
      }
      if (!f.name.toLowerCase().endsWith('.pdf') && f.type !== 'application/pdf') {
        rejected.push(`${f.name} is not a PDF.`);
        continue;
      }
      accepted.push(f);
    }
    this.files.update((arr) => [...arr, ...accepted]);
    this.fileErrors.set(rejected);
  }

  private toMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
  }
}
